import React, { useState } from 'react';
import { useLanguage } from '@/context/LanguageContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { MapPin, Phone, Mail, Clock, MessageCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = "https://gaurav-o0un.onrender.com/api";

const ContactSection = () => {
  const { t } = useLanguage();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contact`, formData);
      toast.success(t('success'), { description: 'Message sent successfully!' });
      setFormData({ name: '', email: '', phone: '', message: '' });
    } catch (error) {
      toast.error(t('error'), { description: error.response?.data?.detail || 'Failed to send message' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="section-spacing" data-testid="contact-section">
      <div className="container-custom">
        <h2 
          className="text-3xl md:text-5xl font-bold text-[#0047AB] text-center mb-4"
          data-testid="contact-title"
        >
          {t('contactTitle')}
        </h2>
        <div className="section-divider mb-12"></div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Info */}
          <div data-testid="contact-info">
            {/* Embedded Google Map */}
            <div className="mb-8 overflow-hidden rounded-xl" data-testid="map-embed">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d1000!2d80.2829195!3d26.4521691!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x399c47275ea317bf%3A0x7f57db7d8a0eb783!2sGupta%20Soap%20Grih%20udyog!5e0!3m2!1sen!2sin!4v1699000000000!5m2!1sen!2sin"
                width="100%"
                height="350"
                style={{ border: 0, borderRadius: '12px' }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Gupta Soap Grih Udyog Location"
              ></iframe>
            </div>

            {/* Get Directions Button */}
            <div className="mb-6">
              <a 
                href="https://www.google.com/maps/dir/?api=1&destination=26.4521691,80.2829195&destination_place_id=ChIJvxejJ95HnDkRg7cdin3bV38" 
                target="_blank" 
                rel="noopener noreferrer"
                data-testid="get-directions-btn"
              >
                <Button className="w-full sm:w-auto bg-[#0047AB] hover:bg-[#003585] flex items-center justify-center gap-2">
                  <MapPin className="w-4 h-4" />
                  Get Directions
                </Button>
              </a>
            </div>

            <div className="space-y-6">
              {/* Address */}
              <div className="flex gap-4">
                <div className="w-12 h-12 bg-[#0047AB]/10 flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-6 h-6 text-[#0047AB]" />
                </div>
                <div>
                  <h4 className="font-bold text-slate-800 mb-1">{t('addressLabel')}</h4>
                  <p className="text-slate-600 text-sm">
                    179-B Dadanagar<br />
                    Co-Operative Industrial Estate<br />
                    Kanpur – 208022<br />
                    Uttar Pradesh, India
                  </p>
                </div>
              </div>

              {/* Phone */}
              <div className="flex gap-4">
                <div className="w-12 h-12 bg-[#0047AB]/10 flex items-center justify-center flex-shrink-0">
                  <Phone className="w-6 h-6 text-[#0047AB]" />
                </div>
                <div>
                  <h4 className="font-bold text-slate-800 mb-1">{t('phone')}</h4>
                  <a href="tel:9919091190" className="text-[#0047AB] hover:underline" data-testid="contact-phone-link">
                    99190-91190
                  </a>
                </div>
              </div>

              {/* Email */}
              <div className="flex gap-4">
                <div className="w-12 h-12 bg-[#0047AB]/10 flex items-center justify-center flex-shrink-0">
                  <Mail className="w-6 h-6 text-[#0047AB]" />
                </div>
                <div>
                  <h4 className="font-bold text-slate-800 mb-1">{t('email')}</h4>
                  <a href="mailto:customercare@guptasoap.com" className="text-[#0047AB] hover:underline" data-testid="contact-email-link">
                    customercare@guptasoap.com
                  </a>
                </div>
              </div>

              {/* Working Hours */}
              <div className="flex gap-4">
                <div className="w-12 h-12 bg-[#0047AB]/10 flex items-center justify-center flex-shrink-0">
                  <Clock className="w-6 h-6 text-[#0047AB]" />
                </div>
                <div>
                  <h4 className="font-bold text-slate-800 mb-1">{t('workingHours')}</h4>
                  <p className="text-slate-600 text-sm">{t('workingHoursValue')}</p>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-wrap gap-4 pt-4">
                <a href="tel:9919091190" data-testid="contact-call-btn">
                  <Button className="btn-primary flex items-center gap-2">
                    <Phone className="w-4 h-4" />
                    {t('callNow')}
                  </Button>
                </a>
                <a href="https://wa.me/919919091190" target="_blank" rel="noopener noreferrer" data-testid="contact-whatsapp-btn">
                  <Button className="bg-green-500 hover:bg-green-600 text-white flex items-center gap-2">
                    <MessageCircle className="w-4 h-4" />
                    WhatsApp
                  </Button>
                </a>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-white p-8 shadow-lg border-t-4 border-[#0047AB]" data-testid="contact-form-container">
            <h3 className="text-xl font-bold text-slate-800 mb-6">{t('sendMessage')}</h3>
            <form onSubmit={handleSubmit} data-testid="contact-form">
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="contact_name">{t('yourName')} *</Label>
                  <Input
                    id="contact_name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="input-field"
                    data-testid="contact-name-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="contact_email">{t('email')} *</Label>
                  <Input
                    id="contact_email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="input-field"
                    data-testid="contact-email-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="contact_phone">{t('phone')} *</Label>
                  <Input
                    id="contact_phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleInputChange}
                    required
                    className="input-field"
                    data-testid="contact-phone-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="contact_message">{t('yourMessage')} *</Label>
                  <Textarea
                    id="contact_message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                    className="input-field min-h-[120px]"
                    data-testid="contact-message-input"
                  />
                </div>

                <Button 
                  type="submit" 
                  className="w-full btn-primary"
                  disabled={isSubmitting}
                  data-testid="contact-submit-btn"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      {t('submitting')}
                    </>
                  ) : (
                    t('sendMessage')
                  )}
                </Button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
